/**
 * **屏幕宿主** —— 把纯状态机（`stack.ts`）变成 Pixi 的图层。
 *
 * ```
 *   world
 *    ├ backdrop      ★ **游戏画面本身**（没有卡牌的那部分）

 *    ├ baseLayer     ★ 唯一可见的那块屏幕（独占）
 *    └ overlayLayer
 *        ├ scrim     遮罩。有叠加层时才出现，负责**挡住底下的点击**
 *        └ ...       叠加层，自下而上
 * ```
 *
 * ## 遮罩为什么必须存在
 *
 * 「设置能盖在游戏之上」要求的不只是"画在上面"，而是**底下的东西点不动**。
 * Pixi 的命中测试是**从前往后**遍历显示树的，所以一块铺满的、`eventMode='static'`
 * 的遮罩就能把落在它下面的所有点击吃掉 —— 这是最省事也最不会错的做法，
 * 比逐个把底下的控件设成 `eventMode='none'` 可靠（后者漏掉一个就是"点到了看不见的按钮"）。
 *
 * 两件事一起做：遮罩挡指针，`onCover(true)` 通知底下那块屏幕（游戏要停输入、停动画）。
 *
 * ## 屏幕是**懒建**的
 *
 * 没进过的屏幕不会建对象 —— 菜单一多，启动时把所有屏幕都建出来会拖慢首屏，
 * 而且它们的构造函数里往往要做布局。
 */

import { Container, Graphics, Rectangle, type Ticker, type Renderer } from 'pixi.js';
import { GameBackdrop } from '../render/game/backdrop';
import { CardArtRegistry, CardFaceCache, specArt } from '../render/card';
import type { CrackMask } from '../render/dying/cracks';
import { viewportOf } from '../render/bg';
import { LOGICAL_HEIGHT, LOGICAL_WIDTH } from '../render/app';
import { BASE } from '../render/palette';
import { stepMotion } from '../render/ui/motion';
import type { KeyRouter } from './keys';
import type { InputTraits } from '../render/input/traits';
import type { LayoutProfile } from '../render/layout';
import { FocusRing, setFocusRing } from '../render/ui/focus';
import { setUiScaleSource } from '../render/ui/target';
import { createGamepadPoller, type GamepadIntent } from '../render/input';
import type { Screen, ScreenContext } from './screen';
import type { SettingsController } from './settings';
import { setLang, t } from '../i18n';
import type { AudioSink, RoomController } from './session';
import type { OnlineRoom } from '../host/online';
import type { EnergyReporter } from '../audio/energy';
import { barAccent, drivesCardPulse, SILENT_MUSIC_CLOCK, type MusicClock } from './beat';
import {
  GLITCH_WALK_RANGE,
  GLITCH_WALK_STEP,
  createGlitchClock,
  glitchStrength,
  GlitchFilter,
} from '../render/bg/glitch';
import { Impact, type ImpactChannels } from '../render/impact';
import { ImpactLayer } from '../render/impactLayer';
import { createWalk } from '../core/walk';
import { closeOverlay, createStack, escape, openOverlay, plan, popScreen, pushScreen, toggleOverlay, type ScreenId, type StackState } from './stack';

export type ScreenFactory = (context: ScreenContext) => Screen;

export interface ScreenHostOptions {
  /** 逻辑世界容器（`createRenderer()` 给的那个） */
  readonly world: Container;
  /** 收指针事件的容器（`app.stage`） */
  readonly input: Container;
  /** 当前输入画像的取值函数（见 `render/input`） */
  readonly traits: () => InputTraits;
  /** 当前版面画像的取值函数（见 `render/layout`） */
  readonly layout: () => LayoutProfile;
  readonly keys: KeyRouter;
  readonly settings: SettingsController;
  readonly room: RoomController;
  readonly audio: AudioSink;
  readonly registry: ScreenContext['registry'];
  readonly selfId: string;
  readonly build: ScreenContext['build'];
  /** 根屏幕的 id */
  readonly rootId: ScreenId;
  /** 渲染器。游戏画面（背景）要它来烘占地遮罩 */
  readonly renderer: Renderer;
  /** 联机那一桌。不给就是单机/热座（见 `ScreenContext.online`） */
  readonly online?: () => OnlineRoom | null;
  /** 动态音乐的落点（见 `ScreenContext.energy`） */
  readonly energy?: EnergyReporter;
  /**
   * 音乐时钟（小节 + 拍）。不给就是 `SILENT_MUSIC_CLOCK`（画面不会跟着
   * 拍子跳）—— 调试页、测试、以及"没有音频"的路径都走这一条。
   */
  readonly music?: MusicClock;
  /**
   * 要不要垫**游戏画面**当背景。
   *
   * 默认开。关掉就是纯黑底，只在调试/对比时有用 ——
   * 评审要的是"菜单用游戏画面当背景"，所以正式路径上它必须是开的。
   */
  readonly backdrop?: boolean;
  /** 背景的种子。固定值 —— 菜单背景不该每次刷新都换一张 */
  readonly seed?: number;
  /**
   * **栈变了（屏幕 / 叠加层）。**
   *
   * 路由层用它把屏幕映射到 URL（见 `ui/router.ts` 与 `main.ts`）。
   * 只在栈**真的**变了时调（`apply` 里 `next === stack` 会提前返回）。
   */
  readonly onStateChange?: (state: StackState) => void;
}

export interface ScreenHost {
  /** 当前栈状态（只读快照） */
  readonly state: StackState;
  /** 注册一个屏幕怎么建 */
  define(id: ScreenId, factory: ScreenFactory): void;
  /**
   * 把第一屏建出来。
   *
   * ⚠️ **必须在注册完屏幕之后再调。**
   *
   * 一开始这里是在 `createScreenHost()` 的末尾直接 `sync()` 的，结果
   * 调用方还没来得及 `define(根屏幕)` 就已经开始建它了 —— 页面直接白屏，
   * 抛的是"没有注册过这个屏幕：title"。真实症状是**什么都不显示**，
   * 而 `tsc` / lint / 单测全是绿的（它们都碰不到 Pixi 的构造顺序）。
   * 所以入口改成显式的一步：`define(...)` 若干次 → `start()`。
   */
  start(): void;
  /** 切到某个屏幕（等价于把栈重置成"根 → ..."） */
  go(id: ScreenId): void;
  push(id: ScreenId): void;
  /** 退一层（屏幕栈）。栈底那层不动 */
  pop(): void;
  /** 开一个叠加层（设置那种）。它盖在当前屏之上，不吃掉栈 */
  openOverlay(id: ScreenId): void;
  closeOverlay(id?: ScreenId): void;
  /** 收到一个按键。返回有没有被吃掉 */
  handleKey(event: Parameters<KeyRouter['dispatch']>[0]): boolean;
  /** 走焦环（诊断 / dev 工具用） */
  focus(): FocusRing;
  /** 每帧。传的是 ticker —— 背景的时间轴要它，屏幕只拿 `deltaMS` */
  update(ticker: Ticker): void;
  /** 重建所有活着的屏幕（UI 倍率变了就调它） */
  rebuildAll(): void;
  /**
   * 背景的**情绪**（亮度 / 色调 / Bar 那一闪）。
   *
   * ⚠️ 只为调试存在（`DEV_TOOLS.render.mood()`）：背景是宿主的私有件，
   * 屏幕拿不到它，而"现在到底偏红还是偏蓝"是调情绪时最想看的那个数。
   * 没垫背景（`backdrop: false`）时返回 `null`。
   */
  mood(): unknown;
  /** 濒死层现在开着吗。调试与冒烟用（评审报过"濒死效果不出现"） */
  dying(): boolean;
  /**
   * 故障滤镜（glitch）现在的状态：开着没有、强度、这一段还剩多久。
   *
   * 冒烟要它：这个效果**大多数时候是关的**，"跑了一会儿没看到"完全不能说明它坏了。
   */
  glitch(): {
    /** 现在挂着没有（**濒死期间**恒为真） */
    readonly on: boolean;
    /** 这一帧真的乘在位移上的强度 */
    readonly strength: number;
    /** 随机漫步现在走到哪儿（相对基准强度的倍率） */
    readonly walk: number;
    /** 现在在爆发期吗（强度 ×3） */
    readonly bursting: boolean;
    /** 从濒死开始过了多久（毫秒）—— 刚出现的那一瞬间是 ×10 */
    readonly sinceMs: number;
    /** 这一轮还剩多久 */
    readonly leftMs: number;
  };
  /**
   * **打击（`render/impact.ts`）现在的状态。**
   *
   * 冒烟要它："冻结"是不是真的把画面定住了、震动有没有归零、闪了几帧 ——
   * 这些从截图上都只能"看着像"，读数才是判据。
   */
  impact(): {
    readonly frozen: boolean;
    readonly ambientHeld: boolean;
    readonly freezeLeftMs: number;
    readonly ambientLeftMs: number;
    readonly sinceHitMs: number;
    readonly trauma: number;
    readonly shake: { readonly x: number; readonly y: number };
    readonly zoom: number;
    readonly flash: number;
    readonly invert: number;
    readonly desat: number;
    readonly aberr: number;
    readonly edge: number;
    /** BGM 的"播放故障"剂量（0..1） */
    readonly fx: number;
    /** BGM 的音量增益倍数（1 = 不动） */
    readonly gain: number;
    /** 世界变换原样报一份（冒烟拿打击前后两份比，必须逐位相同） */
    readonly world: { readonly x: number; readonly y: number; readonly scale: number };
  };
  /**
   * 背景现在是什么档位、画在多小的 FBO 上（见 `render/bg/renderScale.ts`）。
   *
   * 冒烟要它：像素化 / ASCII 都是"看起来变了，但改错了也照样出画面"的那一类，
   * 不量一下就证明不了它真的生效。没垫背景时 `null`。
   */
  field(): {
    readonly scale: number;
    readonly mode: 'plain' | 'pixel' | 'ascii';
    /** 0 档是不是画成 1-bit 硬边（见 `FIELD_HALFTONE_BINARY`） */
    readonly binary: boolean;
    readonly width: number;
    readonly height: number;
  } | null;
  /**
   * 场现在铺在**哪一块**（画布逻辑坐标里的矩形）。没垫背景时 `null`。
   *
   * 冒烟要它：窗口不是 16:10 时这块四边形必须盖住**整块视口**（不是只盖画布）
   * —— "黑边上有场"这件事从像素上量会受"那里本来就暗"的干扰，几何才是判据。
   */
  quad(): readonly [number, number, number, number] | null;
  /**
   * 濒死裂缝那一层。**只为调试存在**（`DEV_TOOLS.render.cracks()`）。
   *
   * 评审报的"裂缝效果你没实现"根因是 `setDying()` 每帧重开一次，而
   * `strength` 那个读数看不出这件事（它在 `begin()` 里立刻就是 1）——
   * 要看的是**贴图里到底有没有线**，所以冒烟得摸到这张 RT。
   */
  cracks(): CrackMask | null;
  destroy(): void;
}

/**
 * **把"打击"的位移与推镜叠在 fit 变换上。**
 *
 * ⚠️ 必须是**叠加**（加 / 乘），不能覆盖：`render/app.ts` 每帧先摆一次 fit
 *    （等比缩放 + 居中），宿主在同一帧的后半段跑，于是正交地在它之上叠一层。
 *    下一帧 `app.ts` 又会把 `position/scale` 写回 fit 值，所以**不会累积**。
 *
 * ⚠️ 推镜要**绕画面中心**放大，不是绕左上角（绕左上角看起来像画面在往右下跑）。
 *    做法是让"逻辑中心落在同一个屏幕点"，也就是让
 *    `screen = position + scale × logical` 这条式子继续成立 ——
 *    背景的 `viewportOf()` 正是它的逆。
 *
 * ⚠️ 所以这里**不用 `pivot`、不转 `rotation`**（评审要的"屏幕震动"里最诱人的
 *    就是转一点，但一转那条式子就不成立：场会铺不满视口、横带也铺不到屏幕两边，
 *    而 `smoke:beat` ⑦ 正盯着这两条）。
 */
function applyImpactTransform(world: Container, channels: ImpactChannels): void {
  const fitScale = world.scale.x;
  const scale = fitScale * channels.zoom;
  const centerX = world.position.x + (LOGICAL_WIDTH / 2) * fitScale;
  const centerY = world.position.y + (LOGICAL_HEIGHT / 2) * fitScale;
  world.scale.set(scale);
  world.position.set(
    centerX - (LOGICAL_WIDTH / 2) * scale + channels.shake.x,
    centerY - (LOGICAL_HEIGHT / 2) * scale + channels.shake.y,
  );
}

/**
 * 叠加层的遮罩浓度。
 *
 * ⚠️ 这不是"菜单的背景" —— 它是**模态**的语义：有东西盖上来时，下面必须
 * 明显变暗，玩家才知道"现在该看上面这一层"。所以它只在叠加层打开时存在。
 * 菜单自己的背景是**游戏画面**（见 `render/game/backdrop.ts`），不在这里画。
 */
const SCRIM_ALPHA = 0.72;

/** 读数里的四舍五入（浮点噪声不该出现在断言里） */
function round(value: number, digits: number): number {
  const factor = 10 ** digits;
  return Math.round(value * factor) / factor;
}

export function createScreenHost(options: ScreenHostOptions): ScreenHost {
  const { world, keys } = options;
  const bounds = { width: LOGICAL_WIDTH, height: LOGICAL_HEIGHT };
  const music = options.music ?? SILENT_MUSIC_CLOCK;

  const root = new Container();
  root.label = 'menu-root';
  world.addChild(root);

  // ---- ★ 背景 = 游戏画面本身（没有卡牌） ----
  //
  // 评审：「UI 界面都不要自己绘制背景，直接让一个没有任何卡牌的游戏画面作为背景，
  // 开始游戏后卡牌再进场就能实现无缝转场了。」
  //
  // ⚠️ 所以这里**没有压暗层**。以前垫过一层 `SHADE_ALPHA` 的遮罩给菜单上的
  //    小字提对比度 —— 但那层遮罩在开局时会整体消失，正好在最需要连续的
  //    那一刻制造一次跳变。可读性交给面板自己的 0.94 底色。
  let backdrop: GameBackdrop | null = null;
  if (options.backdrop ?? true) {
    backdrop = new GameBackdrop({
      renderer: options.renderer,
      width: bounds.width,
      height: bounds.height,
      ...(options.seed === undefined ? {} : { seed: options.seed }),
    });
    root.addChild(backdrop.view);
  }

  const baseLayer = new Container();
  baseLayer.label = 'menu-base';
  const overlayLayer = new Container();
  overlayLayer.label = 'menu-overlays';
  /**
   * 叠加层本体和遮罩**分开两个容器**。
   *
   * 一开始把它们放在同一层里、每次 `removeChildren()` 再重排，结果是遮罩
   * 有时候忘了插回去（它是第一个孩子，清空时一起被清掉了）。
   * 分成两层之后，"遮罩永远在最底下"变成结构保证，不再靠代码顺序。
   */
  const overlayStack = new Container();
  overlayStack.label = 'menu-overlay-stack';

  // ⚠️ 遮罩：铺满、吃掉底下所有点击。见文件头
  const scrim = new Graphics()
    .rect(0, 0, bounds.width, bounds.height)
    .fill({ color: BASE.void, alpha: SCRIM_ALPHA });
  scrim.eventMode = 'static';
  scrim.hitArea = new Rectangle(0, 0, bounds.width, bounds.height);
  overlayLayer.addChild(scrim, overlayStack);

  root.addChild(baseLayer, overlayLayer);

  // ---- 状态 ----
  const factories = new Map<ScreenId, ScreenFactory>();
  const mounted = new Map<ScreenId, Screen>();
  /**
   * 刚建出来、还没收到 `onEnter` 的屏幕。
   *
   * ⚠️ `onEnter` **必须等按键优先级排好之后再调**：屏幕常常在 `onEnter` 里抢焦点
   * （汇合页的输入框就是），抢焦点 = 往按键路由上压一个处理者。
   * 如果那时候图层优先级还没排，这个处理者会被排在屏幕自己**后面**，
   * 于是"打字"得先穿过屏幕的 `onKey` —— 顺序一旦被后来的改动打破，
   * 症状就是"输入框时好时坏"，而且极难复现。
   */
  const pendingEnter: Screen[] = [];
  /**
   * ★ **打击层**：白闪 / 暗角 / 边缘脉冲。
   *
   * ⚠️ 挂在 `options.input`（`app.stage`）而不是 `world`：闪光要铺满**整块画面**
   *    （连窗口不是 16:10 时那一圈黑边一起），而 `world` 只覆盖逻辑画布。
   *    于是它也必须自己跟着画布尺寸走 —— 见 `render()` 里的 `resize`。
   */
  const impact = new Impact();
  /** 上一次推给音频的故障剂量。只在真的变了时推（Tone 的参数不适合每帧摸） */
  let pushedFx = 0;
  /** 上一次推给音频的渐强倍数（同上） */
  let pushedGain = 1;
  const impactLayer = new ImpactLayer();
  options.input.addChild(impactLayer.view);
  let stack: StackState = createStack(options.rootId);
  let releaseKeys: (() => void)[] = [];
  /** `start()` 之前不做任何事 —— 那时候屏幕工厂多半还没注册 */
  let started = false;

  const focus = new FocusRing();
  setFocusRing(focus);
  // 触控最小命中区要知道“逻辑 → CSS”的缩放（见 render/ui/target.ts）
  setUiScaleSource(() => options.layout().scale);
  const gamepad = createGamepadPoller(() => options.settings.get().gamepadLayout);

  const context: ScreenContext = {
    bounds,
    input: options.input,
    traits: options.traits,
    layout: options.layout,
    focus,
    renderer: options.renderer,
    /**
     * ★ **全应用共用一份牌面缓存**（见 `ScreenContext.faces`）。
     *
     * 构造它不烘任何贴图（就是一个空 `Map`），第一次 `get()` 才画 —— 所以
     * 在这里无脑建一份的代价是零，而好处是屏幕切走时**不会**销毁还在用的
     * 牌面纹理（那会刷一屏 `textureSource ... destroyed while still bound`），
     * 再进对局时牌面也还是热的。
     */
    faces: new CardFaceCache(options.renderer, new CardArtRegistry(specArt)),
    keys,
    settings: options.settings,
    room: options.room,
    audio: options.audio,
    registry: options.registry,
    ...(options.online === undefined ? {} : { online: options.online }),
    ...(options.energy === undefined ? {} : { energy: options.energy }),
    music,
    impact: {
      anticipate: () => impact.anticipate(),
      land: () => impact.land(),
      hit: () => {
        impact.hit();
        // 一记合成的低频下潜 + 噪声 crack（不用素材，见 `audio/sfx.ts`）。
        // ⚠️ 只在**主打击**这一帧调一次；没有音频的环境里它是空操作
        options.audio.playImpact();
      },
    },
    selfId: options.selfId,
    build: options.build,
    stack: {
      push: (id) => apply(pushScreen(stack, id)),
      pop: () => apply(popScreen(stack)),
      openOverlay: (id) => apply(openOverlay(stack, id)),
      closeOverlay: (id) => apply(closeOverlay(stack, id)),
      toggleOverlay: (id) => apply(toggleOverlay(stack, id)),
    },
  };

  /** 拿屏幕，没有就建。工厂没注册是**编程错误**，直接抛比默默白屏好 */
  const obtain = (id: ScreenId): Screen => {
    const existing = mounted.get(id);
    if (existing !== undefined) return existing;
    const factory = factories.get(id);
    if (factory === undefined) {
      throw new Error(t('screenHost.unknownScreen', { id }) + t('screenHost.unknownScreenHint'));
    }
    const screen = factory(context);
    mounted.set(id, screen);
    // `onEnter` 延后到 `sync()` 末尾 —— 见 `pendingEnter` 的说明
    pendingEnter.push(screen);
    return screen;
  };

  const unmount = (id: ScreenId): void => {
    const screen = mounted.get(id);
    if (screen === undefined) return;
    mounted.delete(id);
    // 还没 onEnter 就被卸掉：把它的入场券作废，也别调 onExit
    // （没进过场的东西退场时会去关它从没开过的东西）
    const at = pendingEnter.indexOf(screen);
    if (at >= 0) {
      pendingEnter.splice(at, 1);
      screen.destroy();
      return;
    }
    screen.onExit?.();
    screen.destroy();
  };

  /**
   * 按 `plan()` 重排图层。
   *
   * 只做三件事：可见性、按键优先级、`onCover` 通知。
   * **所有层级判断都在 `stack.ts` 里**，这里不做任何决定。
   */
  const sync = (): void => {
    const layer = plan(stack);

    // ---- 图层内容 ----
    baseLayer.removeChildren();
    const screen = obtain(layer.screen);
    baseLayer.addChild(screen.container);
    screen.container.visible = true;

    // overlayLayer 的孩子（遮罩 + 叠加层容器）是**固定的**，只清叠加层容器
    overlayStack.removeChildren();
    const liveOverlays = [...layer.overlays].reverse(); // 自下而上绘制
    for (const id of liveOverlays) {
      overlayStack.addChild(obtain(id).container);
    }
    scrim.visible = liveOverlays.length > 0;

    // ---- 没在用的屏幕卸掉：它们的输入监听器不该继续挂着 ----
    for (const id of [...mounted.keys()]) {
      if (id !== layer.screen && !layer.overlays.includes(id)) unmount(id);
    }

    // ---- 被盖住的那块要"让位" ----
    screen.onCover?.(layer.covered);

    // ---- 按键优先级 = 图层顺序（自下而上压栈，最上面最后压） ----
    for (const release of releaseKeys) release();
    releaseKeys = [];
    // 先压最底下的"ESC 退一层"，它优先级最低
    releaseKeys.push(keys.push({ onKey: (event) => handleEscape(event.key) }));
    // 走焦：方向键 + 确认。优先级**低于**屏幕自己的 handler、高于 ESC ——
    // 所以输入框（自己吃掉按键）仍然优先，而普通屏幕上方向键会被这里接住
    releaseKeys.push(keys.push({ onKey: (event) => handleFocusKey(event) }));
    for (const id of [layer.screen, ...liveOverlays]) {
      const target = mounted.get(id);
      if (target?.onKey === undefined) continue;
      const handler = target.onKey.bind(target);
      releaseKeys.push(keys.push({ onKey: handler }));
    }

    // ---- 最后才 onEnter：这时候抢焦点才抢得到正确的优先级 ----
    if (pendingEnter.length > 0) {
      const entering = pendingEnter.splice(0, pendingEnter.length);
      for (const screen of entering) screen.onEnter?.();
    }
  };

  const handleEscape = (key: string): boolean => {
    if (key !== 'Escape') return false;
    apply(escape(stack));
    return true;
  };

  /** 方向键 + Enter/Space 走焦。有可聚焦控件时才接；否则让键继续往下传 */
  const handleFocusKey = (event: { key: string }): boolean => {
    if (focus.size === 0) return false;
    switch (event.key) {
      case 'ArrowUp':
        focus.move('up');
        return true;
      case 'ArrowDown':
        focus.move('down');
        return true;
      case 'ArrowLeft':
        focus.move('left');
        return true;
      case 'ArrowRight':
        focus.move('right');
        return true;
      case 'Enter':
      case ' ':
        return focus.activate();
      default:
        return false;
    }
  };

  /** 手柄意图：导航走走焦，`menu`/`tracker`/`endTurn` 转发给当前屏幕 */
  const handleGamepad = (intent: GamepadIntent): void => {
    switch (intent) {
      case 'up':
        focus.move('up');
        return;
      case 'down':
        focus.move('down');
        return;
      case 'left':
        focus.move('left');
        return;
      case 'right':
        focus.move('right');
        return;
      case 'confirm':
        focus.activate();
        return;
      case 'back':
        apply(escape(stack));
        return;
      default: {
        const active = stack.screens[stack.screens.length - 1];
        const ids = [...stack.overlays].reverse();
        if (active !== undefined) ids.push(active);
        for (const id of ids) {
          if (mounted.get(id)?.onGamepad?.(intent) === true) return;
        }
      }
    }
  };

  const apply = (next: StackState): void => {
    if (next === stack) return;
    stack = next;
    sync();
    // 路由层在这里把新栈映射成 URL（见 ScreenHostOptions.onStateChange）
    options.onStateChange?.(next);
  };

  // UI 倍率 / 语言变了：整屏重建。菜单很便宜，而"让每个控件自己重排"是
  // 一堆只在改倍率时才跑到的代码 —— 那是最容易腐烂的那种。
  // 语言要先同步进 i18n，重建时 `t()` 才拿得到新语言。
  const unsubscribe = options.settings.subscribe((next, previous) => {
    setLang(next.lang);
    if (next.uiScale === previous.uiScale && next.lang === previous.lang) return;
    host.rebuildAll();
  });

  /**
   * **音乐 → 画面。** 两条流、三处落点：
   *
   * | 落点 | 跟哪条流 | 为什么 |
   * | --- | --- | --- |
   * | 背景中心脉冲 | **bar** | 每拍推一圈会糊成一团；它描的是"小节"这种大呼吸 |
   * | 濒死层重音 | **bar** | 同上，一下一下地敲才有警示感 |
   * | 牌面脉动（手牌 / 牌背） | bar，**终局改 beat** | 见 `drivesCardPulse()` |
   *
   * ⚠️ 挂在这里而不是让每一屏自己去 `music.onBar()`：那样每屏都要在
   * `onEnter/onExit` 里配对地挂上摘掉，而**漏摘**的后果是一屏已经销毁了
   * 还在收拍子（`hand.pulse()` 打在已销毁的对象上）。宿主这一处挂一次，
   * 生命周期正好和它管的那棵树对齐。
   *
   * ⚠️ 强度（乐句头 / 其余小节）和"终局跟拍"这两条判断都在**这里** ——
   * 渲染层和屏幕都不该认识"乐句"或"终局"的概念，它们只知道
   * "跳一下"和"跳多重"。
   */
  /**
   * **故障滤镜（glitch）。**
   *
   * 评审：
   *
   * > 写个 glitch 滤镜，表现出轻度的色块错位，画面撕裂等现象，不改变颜色。
   *
   * 以及（这一轮）：
   *
   * > glitch 效果我希望改为常态存在，但强度极为轻微，且强度数值每帧进行随机
   * > 漫步……而原有的"每隔一段时间触发"，改为"每隔一段时间强度 x3"。
   *
   * 而且（评审澄清）：
   *
   * > 我说的常态存在是指**濒死期间**常态存在，而不是整个游戏运行期间常态存在。
   *
   * 所以：**挂/摘仍然按 `isDying`**，但在濒死那段时间里它**一直是开的**：
   *
   * ```
   *   强度 = GLITCH_BASE_STRENGTH × 漫步值 × (爆发期 ? 3 : 1)
   * ```
   *
   * ⚠️ 它挂在 **`world`** 上（不是背景那一层）：评审说的是"画面撕裂"，
   *    牌、UI、背景一起被搬走才像信号出了问题。
   *
   * ⚠️ **不在濒死时摘掉**（`world.filters = []`）：整屏多一道过滤是实打实的
   *    开销，而这个效果只在濒死那几秒存在。挂/摘只发生在进出濒死那一刻。
   */
  const glitch = new GlitchFilter();
  const glitchClock = createGlitchClock();
  const glitchWalk = createWalk({
    min: GLITCH_WALK_RANGE.min,
    max: GLITCH_WALK_RANGE.max,
    initial: 1,
    maxStep: GLITCH_WALK_STEP,
    // ⚠️ 纯视效：故障滤镜的"信号抽搐"不需要可复现（见 `docs/onboarding.md` §4.4 第 5 条）
    random: Math.random,
  });
  let glitchOn = false;
  /**
   * **从濒死开始过了多久**（毫秒，走 `deltaMS`）。
   *
   * ⚠️ 只用来算"刚出现那一瞬间的 ×10"（`glitchOnsetBoost`）：进濒死时**归零**，
   *    离开濒死不动（下一次进来再归零 —— 不归零的话第二次濒死就没有那一下了）。
   */
  let dyingElapsedMs = 0;
  const setGlitchOn = (on: boolean): void => {
    if (on === glitchOn) return;
    world.filters = on ? [glitch] : [];
    glitchOn = on;
  };

  const dispatchPulse = (tick: Parameters<NonNullable<Screen['onPulse']>>[0]): void => {
    const layer = plan(stack);
    mounted.get(layer.screen)?.onPulse?.(tick);
    for (const id of layer.overlays) mounted.get(id)?.onPulse?.(tick);
  };
  /** 是不是终局。**只有评分表说了算** —— 界面不许自己判 */
  const inFinale = (): boolean => options.energy?.isFinal === true;

  const offBars = music.onBar((tick) => {
    backdrop?.barTick(barAccent(tick));
    /**
     * 屏幕边缘跟着小节红一下（辉光也在一起跳，但"边框"是另一层）。
     *
     * ⚠️ **必须自己判濒死**：这一条是"打击"那一套里的东西，而 bar 事件
     *    在**整局**都在发（手牌脉动、背景中心脉冲本来就该常态跟拍子）。
     *    漏了这道闸门的样子是：平时每小节屏幕四角都红一下 —— 评审看到的
     *    就是"有些 Bar 事件没做濒死检查，常态生效"。
     */
    if (options.energy?.isDying === true) impact.bar();
    if (drivesCardPulse('bar', inFinale())) {
      dispatchPulse({ kind: 'bar', bar: tick.bar, beatInBar: 0 });
    }
  });
  const offBeats = music.onBeat((tick) => {
    if (drivesCardPulse('beat', inFinale())) {
      dispatchPulse({ kind: 'beat', bar: tick.bar, beatInBar: tick.beatInBar });
    }
  });

  const host: ScreenHost = {
    get state() {
      return stack;
    },
    define(id, factory) {
      factories.set(id, factory);
    },
    start() {
      if (started) return;
      started = true;
      sync();
    },
    go(id) {
      apply(pushScreen(stack, id));
    },
    push(id) {
      apply(pushScreen(stack, id));
    },
    pop() {
      apply(popScreen(stack));
    },
    openOverlay(id) {
      apply(openOverlay(stack, id));
    },
    closeOverlay(id) {
      apply(closeOverlay(stack, id));
    },
    handleKey(event) {
      if (!started) return false;
      // 屏幕自己的快捷键（比如数字键选菜单项）走这儿。ESC 由 `handleEscape` 那条兜底
      return keys.dispatch(event);
    },
    focus() {
      return focus;
    },
    update(ticker) {
      if (!started) return;

      // 手柄：轮询 → 边沿意图。方向/确认/返回走走焦，其余转发给当前屏幕
      for (const intent of gamepad.poll()) handleGamepad(intent);

      /**
       * ★ **打击的时钟先走。**
       *
       * ⚠️ 它必须是这一帧的**第一件事**：冻结（`channels.frozen`）要挡住下面
       *    所有动画；而世界变换（震动 + 推镜）必须赶在 `backdrop.update` 之前
       *    摆好 —— 背景的四边形是从 `world` 反推的（`viewportOf`），先摆它，
       *    场才会跟着一起震（不然黑边上会露出一条没铺到的地方）。
       *
       * 病根与时间表在 `render/impact.ts`，方案在 `docs/dying-impact.md`。
       */
      const dying = options.energy?.isDying === true;
      impact.update(ticker.deltaMS / 1000, dying);
      const channels = impact.channels(dying);
      applyImpactTransform(world, channels);
      // BGM 的"播放故障"剂量：变了才推（0.01 的死区，免得每帧都摸 Tone 参数）
      if (Math.abs(channels.fx - pushedFx) > 0.01 || channels.fx === 0) {
        pushedFx = channels.fx;
        options.audio.setBgmFx(channels.fx);
      }
      // 渐强（riser）：变了才推。它一路从 1 爬到 1.45，再落回 1
      if (Math.abs(channels.gain - pushedGain) > 0.001) {
        pushedGain = channels.gain;
        options.audio.setBgmBoost(channels.gain);
      }
      // 颜色那三项塞进现有那趟故障滤镜（不新增 pass），每帧写一遍最省心
      glitch.setColors(channels.aberr, channels.desat, channels.invert);
      impactLayer.resize(options.renderer.screen.width, options.renderer.screen.height);
      impactLayer.render(channels);

      const layer0 = plan(stack);
      // ⚠️ **"世界静止"**：打击之后的一小段里，背景 / 横带 / 裂缝 / 呼吸一起停住
      //    （牌还在撑满）。威胁感来自"运动变少"，不是变多。
      if (!channels.frozen && !channels.ambientHeld) {
        // ⚠️ 占地必须先算（`backdrop.update` 内部保证顺序），而且卡的来源是
        // **当前那一屏** —— 菜单屏不实现 `sceneCards`，于是背景一个洞都不挖，
        // 开局时发生的事只有"卡牌进场"，没有换管线
        //
        // ⚠️ 世界变换每帧读一次传下去：标量场要**铺满整块视口**（窗口不是
        //    16:10 时黑边上也要有场，不能空一块），而"视口有多大"只有这里知道
        //    （`render/app.ts` 是唯一设它的地方）
        backdrop?.update(
          ticker,
          mounted.get(layer0.screen)?.sceneCards?.() ?? [],
          viewportOf(options.world, options.renderer.screen),
        );
      }
      // ★ 情绪：energy 决定背景的色调/亮度**和播放速度**（`bg/mood.ts`）。
      //   没接线（没有音频）时按 0 算 —— 也就是"中性偏暗"那一档
      // ⚠️ 用 `visualEnergy`（画面那一份：濒死/终局会抬起来），
      //    不是 `energy`（真实值，规则读的那一份）—— 见 `EnergySnapshot`
      backdrop?.setEnergy(options.energy?.visualEnergy ?? 0);
      /**
       * ★ **濒死层就靠这一行开合。**
       *
       * ⚠️ 这一段以前漏了 —— `GameBackdrop.setDying()` 从来没有人调，
       * 于是"抽到异常牌"时只有界面上的字变了，红斜杠横带、呼吸辉光、
       * 裂缝遮罩一个都不出现（评审报的正是这个）。
       *
       * 谁说了算：**评分表的 `isDying`**（它本来就是"视图里我在不在濒死"
       * 那一条）。宿主只是转手 —— 界面不许自己判濒死。
       */
      backdrop?.setDying(dying);
      setGlitchOn(dying);

      /**
       * ⚠️ **冻结期间到此为止。**
       *
       * 上面那几行是**状态**（在不在濒死、情绪、滤镜挂没挂）—— 它们必须照推：
       * 打击就发生在"刚进濒死"那一帧，不推的话"打击"和"红世界亮起来"会差
       * 整整一个冻结时长（评审要的正是这两件事**同时**发生）。
       *
       * 下面那些是**时间**（故障的时间表、控件补间、屏幕每帧）—— 冻结期间
       * 一帧都不推进，于是画面真的是定格的（"这一帧"和"上一帧"逐位相同）。
       *
       * 输入不受影响（指针/键盘是事件驱动的），音乐更不受影响（它挂在音频
       * 时钟上）—— 冻结只冻画面。见 `docs/dying-impact.md` §4.1 S2。
       */
      if (channels.frozen) return;

      /**
       * ★ **故障滤镜的时间表。** 只有濒死期间才走；不生效时滤镜是摘掉的，
       * 所以平静的时候一分钱都不花。
       */
      /**
       * ★ **只有濒死期间开**（评审的澄清），但那段时间里它**一直是开的**：
       * 强度 = 基准 × 随机漫步 × (爆发期 ? 3 : 1)。漫步每帧走一小步。
       *
       * ⚠️ 不在濒死时**连时间表和漫步都不推进**（`reset()`）：下一次抽到致命牌
       *    从"整段全新的空闲 + 基准强度"开始，而不是接着上一轮的进度。
       */
      if (dying && !glitchOn) dyingElapsedMs = 0; // 刚进濒死：这一下的时钟归零
      if (dying) {
        dyingElapsedMs += ticker.deltaMS;
        // ⚠️ 不用传屏幕尺寸了：区域里的物理像素数由着色器问驱动要
        //    （见 `render/bg/glitch.ts` 的 `uGlitchA` 与 `Filter.resolution`）
        glitch.setFrame(
          glitchStrength(
            glitchWalk.next(),
            glitchClock.advance(ticker.deltaMS),
            dyingElapsedMs,
          ),
        );
      } else {
        glitchClock.reset();
        glitchWalk.reset();
        // 读数要诚实：不在濒死时强度是 0（不然读出来的是上一轮留下的那个数）
        glitch.setFrame(0);
      }

      // ⚠️ **每帧只推一次**控件的过渡动画。项目里只有这一个 ticker，
      // 所以"每帧"没有第二个来源 —— 多推一次等于动画速度翻倍，
      // 而那种 bug 只在"改完 UI 倍率重建之后"才现形（见 motion.ts）
      stepMotion(ticker.deltaMS);
      const layer = plan(stack);
      mounted.get(layer.screen)?.update?.(ticker.deltaMS);
      mounted.get(layer.screen)?.updateFrame?.(ticker);
      for (const id of layer.overlays) {
        mounted.get(id)?.update?.(ticker.deltaMS);
        mounted.get(id)?.updateFrame?.(ticker);
      }
    },
    impact() {
      const channels = impact.channels(options.energy?.isDying === true);
      return {
        frozen: channels.frozen,
        ambientHeld: channels.ambientHeld,
        freezeLeftMs: Math.round(impact.freezeLeftMs),
        ambientLeftMs: Math.round(impact.ambientLeftMs),
        sinceHitMs: Math.round(impact.sinceHitMs),
        trauma: round(impact.trauma, 3),
        shake: { x: round(channels.shake.x, 2), y: round(channels.shake.y, 2) },
        zoom: round(channels.zoom, 4),
        flash: round(channels.flash, 3),
        invert: round(channels.invert, 3),
        desat: round(channels.desat, 3),
        aberr: round(channels.aberr, 2),
        edge: round(channels.edge, 3),
        fx: round(channels.fx, 3),
        gain: round(channels.gain, 3),
        /**
         * **世界变换原样报一份。**
         *
         * ⚠️ 冒烟靠它验"震动会归零、而且不漂"：拿打击**之前**那一份和衰减完
         *    之后那一份比，**逐位相同**才算过（漂一点点就是画面永久偏了，
         *    那比不震更糟）。这里故意**不去反算 fit 值** —— 那等于把
         *    `render/app.ts` 的算法抄第二遍，两处各算一次正是这个项目的老毛病。
         */
        world: {
          x: round(world.position.x, 3),
          y: round(world.position.y, 3),
          scale: round(world.scale.x, 6),
        },
      };
    },
    mood() {
      return backdrop === null ? null : backdrop.background.mood;
    },
    dying() {
      return backdrop?.isDying ?? false;
    },
    glitch() {
      return {
        on: glitchOn,
        strength: Math.round(glitch.strength * 1000) / 1000,
        walk: Math.round(glitchWalk.value * 1000) / 1000,
        bursting: glitchClock.bursting,
        sinceMs: Math.round(dyingElapsedMs),
        leftMs: Math.round(glitchClock.phaseLeftMs),
      };
    },
    cracks() {
      return backdrop?.cracks ?? null;
    },
    field() {
      if (backdrop === null) return null;
      const background = backdrop.background;
      return {
        scale: background.renderScale,
        mode: background.mode,
        binary: background.halftoneBinary,
        ...background.fieldSize,
      };
    },
    quad() {
      return backdrop?.background.quad ?? null;
    },
    rebuildAll() {
      if (!started) return;
      // 能自己重排的就重排（屏幕的容器还是同一个，顺手保住了它内部的临时状态，
      // 比如汇合页输入框里已经打了一半的房间号）；没实现的才销毁重建
      for (const [id, screen] of [...mounted]) {
        if (screen.rebuild !== undefined) screen.rebuild();
        else unmount(id);
      }
      sync();
    },
    destroy() {
      unsubscribe();
      offBars();
      offBeats();
      for (const release of releaseKeys) release();
      releaseKeys = [];
      for (const id of [...mounted.keys()]) unmount(id);
      focus.clear();
      setFocusRing(null);
      setUiScaleSource(null);
      world.filters = [];
      world.rotation = 0;
      impactLayer.destroy();
      glitch.destroy();
      backdrop?.destroy();
      root.destroy({ children: true });
    },
  };

  return host;
}
